import {HoistModel, managed, TaskObserver, XH} from '@xh/hoist/core';
import {action, observable, observableRef, runInAction} from '@xh/hoist/mobx';
import {asServerException, getDefaultOptions} from '../../../../../core/ServiceUtils';
import {GridModel} from '@xh/hoist/cmp/grid';
import {ImportResults, Transaction} from '../../../../data/DataTypes';
import {wait} from '@xh/hoist/promise';
import {fmtNumber} from '@xh/hoist/format';

export class PositionImporterModel extends HoistModel {
    apiTask = TaskObserver.trackLast();
    @observable accessor isOpen: boolean;
    @observableRef accessor warnings = [];
    @managed gridModel = this.createGridModel();

    @action open(file: File) {
        this.warnings = [];
        this.gridModel.clear();

        this.isOpen = true;
        this.processFile(file).linkTo(this.apiTask);
    }

    @action close() {
        this.isOpen = false;
    }

    async import() {
        try {
            var response = await XH.fetchService
                .postJson({
                    url: `${XH.getConf('svcApiUrl')}/positionimport/import`,
                    body: this.gridModel.store.allRecords.map(r => r.raw),
                    ...getDefaultOptions()
                })
                .linkTo(this.apiTask);

            this.close();
            XH.successToast(response.status);
        } catch (e) {
            XH.handleException(asServerException(e));
        }
    }

    private async processFile(file: File) {
        let resolver: (v) => void;
        const ret = new Promise(r => (resolver = r));
        wait().then(() => {
            try {
                var reader = new FileReader();
                reader.readAsArrayBuffer(file);
                reader.onload = async e => {
                    try {
                        const content = e.target.result as ArrayBuffer,
                            uintArray = new Uint8Array(content),
                            dataArray = [];
                        for (let i = 0; i < uintArray.length; i++) dataArray.push(uintArray[i]);

                        const importResults: ImportResults = await XH.fetchService.postJson({
                            url: `${XH.getConf('svcApiUrl')}/positionimport/upload`,
                            body: {fileName: file.name, contents: dataArray},
                            ...getDefaultOptions()
                        });
                        this.gridModel.loadData(importResults.transactions);
                        runInAction(() => (this.warnings = importResults.warnings));
                    } catch (e) {
                        XH.handleException(asServerException(e));
                    } finally {
                        resolver(null);
                    }
                };
            } catch (e) {
                XH.handleException(e);
                resolver(null);
            }
        });
        return ret;
    }

    private createGridModel(): GridModel {
        return new GridModel({
            store: {
                idSpec: () => XH.genId(),
                processRawData: (data: Transaction) => {
                    return {
                        ...data,
                        portfolioName: XH.portfolioService.portfoliosById.get(data.portfolioId).portfolioName
                    };
                }
            },
            columns: [
                {field: 'securityId', width: 100},
                {field: 'portfolioId', hidden: true},
                {field: 'portfolioName', flex: 1},
                {field: 'transactionDate', displayName: 'As Of', width: 100},
                {field: 'type'},
                {field: 'ticker'},
                {
                    field: 'quantity',
                    align: 'right',
                    width: 100,
                    renderer: v => fmtNumber(v, {precision: 2, ledger: true, colorSpec: true})
                }
            ]
        });
    }
}
